import React from 'react';
import { useLocation } from 'react-router';
import Header from 'layout/Header';
import { IndependentIraUserCheckPayload } from 'services/requests';
import { useNavigate } from 'hooks/routes/useNavigate';
import { useIraAccountForm } from 'hooks/forms/useIraAccountsForm';
import { useAddIndependentIraUser } from 'hooks/api/independentIra';
import { useGetProfileList } from 'hooks/api/profiles';
import { useGetProfile } from 'hooks/api/login';
import { useGetDefaultUserManagers } from 'hooks/api/userManager';
import { formatInputDateForBE, mapCustodianTypeValueToKey, parseInputDate } from 'utils';
import { USER_STATUS } from 'constants/status';
import Button from 'components/Button';
import { EntityStatus } from 'components/Label';
import messages from './IndependentIraNew.messages';
import styles from './IndependentIraNew.module.css';
import IndependentIraForm from '../components/IndependentIraForm';
import { mapIndependentIraTypeValueToKey } from '../IraIndependentIra.utils';

const protectFieldsInState = (state: unknown): state is IndependentIraUserCheckPayload =>
  !!(state && (state as IndependentIraUserCheckPayload));

export default function IndependentIraNew() {
  const { state } = useLocation();
  const profileLocationState = protectFieldsInState(state) ? state : null;
  const { toBack } = useNavigate();
  const form = useIraAccountForm(profileLocationState);
  const { handleSubmit, getValues } = form;

  const { mutateAsync: addIndependentIraUser, isLoading } = useAddIndependentIraUser();
  const { data: otherAccounts } = useGetProfileList(profileLocationState?.email);
  const { data: profileData } = useGetProfile();
  const { defaultEtzFee, defaultCustodyFeePercentageIraClub } = profileData || {};

  const { data: defaultUserManagersData } = useGetDefaultUserManagers();

  const onAddIraClick = () => {
    const formValues = getValues();
    addIndependentIraUser({
      ...formValues,
      profileType: mapIndependentIraTypeValueToKey(formValues.profileType) ?? '',
      custodianType: mapCustodianTypeValueToKey(formValues.custodianType) ?? '',
      annualFee: +formValues.annualFee,
      custodyFeeAbsolute: formValues.custodyFeeAbsolute ?? '',
      custodyFeePercentage: formValues.custodyFeePercentage ?? '',
      nextBillingDate: formValues.nextBillingDate
        ? formatInputDateForBE(parseInputDate(formValues.nextBillingDate))
        : '',
    });
  };
  const onCancelClick = () => {
    toBack();
    form.reset();
  };

  return (
    <>
      <Header
        title={messages.newIra}
        complexComponent={true}
        label={{
          status: profileLocationState?.profileStatus ?? USER_STATUS.draft,
          entityStatus: EntityStatus.User,
        }}
      >
        <Button
          onClick={onCancelClick}
          variant={'outline'}
          className={styles.iraAccountsProfile__buttonInvite}
        >
          {messages.cancelButton}
        </Button>
        <Button
          onClick={handleSubmit(onAddIraClick)}
          isLoading={isLoading}
          className={styles.iraAccountsProfile__buttonInvite}
        >
          {messages.addIraButton}
        </Button>
      </Header>
      <IndependentIraForm
        form={form}
        isNew={true}
        email={profileLocationState?.email}
        profileStatus={profileLocationState?.profileStatus || USER_STATUS.draft}
        hasOtherAccount={!!profileLocationState?.profileStatus}
        profileList={otherAccounts}
        defaultEtzFee={defaultEtzFee}
        defaultCustodyFeePercentageIraClub={defaultCustodyFeePercentageIraClub}
        custodianType={profileLocationState?.custodianType}
        defaultUserManager={defaultUserManagersData}
        // User can edit the custodian when there is no default value
        shouldEditCustodian={!profileLocationState?.custodianType}
      />
    </>
  );
}
